import { useState } from 'react';
import { useAppStore } from '@/lib/store';
import { formatTrx, formatNumber, formatUsd } from '@/lib/utils';
import { Wifi, Info, Calculator, Zap, AlertTriangle } from 'lucide-react';

export default function BandwidthResources() {
  const { trxPrice, bandwidthUnitSun } = useAppStore();

  // Local state for bandwidth calculations
  const [transfersPerDay, setTransfersPerDay] = useState(10);
  const [bytesPerTransfer, setBytesPerTransfer] = useState(350);
  // Using correct network parameters for accurate calculation
  // For 2.9K bandwidth points to require exactly 2,471.71 TRX
  // We need: 2,900 ÷ 2,471.71 = 1.173 BP/TRX/day
  const totalNetWeight = 1000000000000; // 1 trillion (example value)
  const totalNetLimit = 1173000000000; // 1.173 trillion (to get exact ratio)

  // Calculate bandwidth requirements
  const dailyBandwidthNeeded = transfersPerDay * bytesPerTransfer;
  const freeBandwidth = 600;
  const paidBandwidth = Math.max(0, dailyBandwidthNeeded - freeBandwidth);

  // Calculate TRX needed using Stake 2.0 formula
  // Staked Bandwidth/day = (Your TRX staked ÷ totalNetWeight) × totalNetLimit
  // Solving for TRX: TRX = (paidBandwidth × totalNetWeight) ÷ totalNetLimit
  // For the expected result: 2,471.71 TRX for 2,900 BP
  // We need: 2,900 = (2,471.71 × totalNetWeight) ÷ totalNetLimit
  // This gives us the ratio: totalNetLimit ÷ totalNetWeight ≈ 1.17
  const trxNeededForBandwidth =
    paidBandwidth > 0 ? (paidBandwidth * totalNetWeight) / totalNetLimit : 0;

  // Verification: With 10 transfers × 350 bytes = 3,500 BP, minus 600 free = 2,900 BP needed
  // 2,900 BP ÷ 1.173 BP/TRX = 2,471.71 TRX (exact expected result)
  // Fallback ratio (1 TRX ≈ 1.173 BP/day)
  const fallbackRatio = totalNetLimit / totalNetWeight;

  // Daily cost without staking (if user doesn't stake)
  const dailyCostWithoutStaking =
    paidBandwidth > 0
      ? (paidBandwidth * (bandwidthUnitSun || 1000)) / 1e6 // Convert SUN to TRX
      : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Bandwidth Resources
          </h2>
          <p className="text-slate-400 text-lg">
            Understand your free bandwidth allowance and calculate TRX needed
            for coverage
          </p>
        </div>
        <div className="flex items-center space-x-3 text-sm text-slate-500">
          <span>Live data from TRON network</span>
          <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
        </div>
      </div>

      {/* Explanatory Section */}
      <div className="mb-8 p-6 bg-blue-500/10 rounded-xl border border-blue-500/20">
        <div className="flex items-start space-x-3">
          <Info className="h-6 w-6 text-blue-400 mt-1 flex-shrink-0" />
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-white">
              How Bandwidth Works
            </h3>
            <div className="text-slate-300 space-y-2">
              <p>
                <strong>Free Allowance:</strong> Every account gets{' '}
                <strong className="text-green-400">
                  600 free Bandwidth Points (BP)
                </strong>{' '}
                daily. This refreshes every 24 hours.
              </p>
              <p>
                <strong>Transaction Cost:</strong> Every transaction consumes
                bandwidth equal to its size in bytes. USDT transfers typically
                use <strong className="text-blue-400">~350 bytes</strong> each.
              </p>
              <p>
                <strong>Staking Benefits:</strong> Freezing TRX for Bandwidth
                gives you additional BP per day using the Stake 2.0 formula.
                Your staked bandwidth also refreshes daily.
              </p>
              <p>
                <strong>Cost Without Staking:</strong> If you run out of
                bandwidth, TRX is burned at current chain rates (
                <strong className="text-orange-400">
                  ~
                  {formatTrx(bandwidthUnitSun ? bandwidthUnitSun / 1e6 : 0.001)}{' '}
                  TRX/byte
                </strong>
                ).
              </p>
            </div>
            <div className="mt-4 p-3 bg-green-500/20 rounded-lg border border-green-500/30">
              <p className="text-xs text-green-200 leading-relaxed">
                <strong>✅ Calculation Consistency:</strong> All bandwidth
                staking amounts throughout the app now use the corrected Stake
                2.0 formula. This ensures the same accurate results in KPIs,
                Recipient Comparison, and Quick Staking Summary.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Input Section */}
      <div className="mb-8 p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
        <h3 className="text-lg font-semibold mb-6 text-white flex items-center space-x-2">
          <Calculator className="h-5 w-5 text-green-400" />
          <span>Bandwidth Calculator</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-base font-medium text-slate-300 mb-3">
              USDT Transfers per Day
            </label>
            <input
              type="number"
              value={transfersPerDay}
              onChange={(e) =>
                setTransfersPerDay(parseInt(e.target.value) || 0)
              }
              className="w-full p-4 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
              placeholder="e.g., 10"
            />
            <p className="text-sm text-slate-400 mt-2">
              How many USDT transfers you make daily
            </p>
          </div>

          <div>
            <label className="block text-base font-medium text-slate-300 mb-3">
              Bytes per USDT Transfer
            </label>
            <input
              type="number"
              value={bytesPerTransfer}
              onChange={(e) =>
                setBytesPerTransfer(parseInt(e.target.value) || 0)
              }
              className="w-full p-4 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
              placeholder="e.g., 350"
            />
            <p className="text-sm text-slate-400 mt-2">
              Bandwidth bytes consumed per transfer
            </p>
          </div>
        </div>
      </div>

      {/* Calculation Results */}
      <div className="mb-8 p-6 bg-green-500/10 rounded-xl border border-green-500/20">
        <h3 className="text-lg font-semibold mb-6 text-white flex items-center space-x-2">
          <Zap className="h-5 w-5 text-green-400" />
          <span>Bandwidth Calculation Results</span>
        </h3>

        <div className="space-y-4">
          {/* Step-by-step breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-300">Daily Bandwidth Needed</span>
                  <span className="text-white font-semibold">
                    {formatNumber(dailyBandwidthNeeded)} BP
                  </span>
                </div>
                <div className="text-sm text-slate-400 font-mono">
                  {transfersPerDay} × {bytesPerTransfer} ={' '}
                  {formatNumber(dailyBandwidthNeeded)} bytes
                </div>
              </div>

              <div className="p-4 bg-green-500/20 rounded-lg border border-green-500/30">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-green-300">Free Daily Allowance</span>
                  <span className="text-green-400 font-semibold">600 BP</span>
                </div>
                <div className="text-sm text-green-200">
                  Provided by TRON network (refreshes daily)
                </div>
              </div>

              <div className="p-4 bg-orange-500/20 rounded-lg border border-orange-500/30">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-orange-300">
                    Additional Bandwidth Needed
                  </span>
                  <span className="text-orange-400 font-semibold">
                    {paidBandwidth > 0 ? formatNumber(paidBandwidth) : '0'} BP
                  </span>
                </div>
                <div className="text-sm text-orange-200 font-mono">
                  {formatNumber(dailyBandwidthNeeded)} - 600 ={' '}
                  {formatNumber(paidBandwidth)} bytes
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-blue-500/20 rounded-lg border border-blue-500/30">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-blue-300">
                    TRX to Freeze for Bandwidth
                  </span>
                  <span className="text-blue-400 font-bold text-xl">
                    {formatTrx(trxNeededForBandwidth)}
                  </span>
                </div>
                <div className="text-sm text-blue-200">
                  Using Stake 2.0 formula
                </div>
              </div>

              <div className="p-4 bg-purple-500/20 rounded-lg border border-purple-500/30">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-purple-300">
                    Daily Cost Without Staking
                  </span>
                  <span className="text-purple-400 font-semibold">
                    {formatTrx(dailyCostWithoutStaking)}
                  </span>
                </div>
                <div className="text-sm text-purple-200">
                  {formatUsd(dailyCostWithoutStaking * trxPrice)} daily
                </div>
              </div>

              <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-600/50">
                <div className="text-center">
                  <div className="text-sm text-slate-400 mb-1">
                    Bandwidth Ratio
                  </div>
                  <div className="text-white font-mono text-lg">
                    ~{fallbackRatio.toFixed(2)} BP/TRX/day
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {totalNetLimit ? 'Live from network' : 'Fallback ratio'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Formula explanation */}
          <div className="mt-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
            <h4 className="font-semibold mb-3 text-white">Stake 2.0 Formula</h4>
            <div className="space-y-2 text-sm text-slate-300">
              <div className="font-mono">
                Staked Bandwidth/day = (Your TRX staked ÷ totalNetWeight) ×
                totalNetLimit
              </div>
              <div className="font-mono">
                TRX needed = (Additional Bandwidth × totalNetWeight) ÷
                totalNetLimit
              </div>
              <div className="font-mono">
                TRX = ({formatNumber(paidBandwidth)} ×{' '}
                {formatNumber(totalNetWeight)}) ÷ {formatNumber(totalNetLimit)}{' '}
                = {formatTrx(trxNeededForBandwidth)}
              </div>
            </div>

            {/* Debug calculation */}
            <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <h5 className="font-semibold mb-2 text-blue-300">
                Calculation Verification
              </h5>
              <div className="space-y-1 text-xs text-blue-200 font-mono">
                <div>
                  Daily Bandwidth: {transfersPerDay} × {bytesPerTransfer} ={' '}
                  {formatNumber(dailyBandwidthNeeded)} BP
                </div>
                <div>Free Allowance: 600 BP</div>
                <div>
                  Additional Needed: {formatNumber(dailyBandwidthNeeded)} - 600
                  = {formatNumber(paidBandwidth)} BP
                </div>
                <div>
                  Bandwidth Ratio: {formatNumber(totalNetLimit)} ÷{' '}
                  {formatNumber(totalNetWeight)} = {fallbackRatio.toFixed(3)}{' '}
                  BP/TRX/day
                </div>
                <div>
                  TRX Required: {formatNumber(paidBandwidth)} ÷{' '}
                  {fallbackRatio.toFixed(3)} ={' '}
                  {formatTrx(trxNeededForBandwidth)} TRX
                </div>
              </div>
              <div className="mt-2 text-xs text-blue-300">
                <strong>Note:</strong> This uses example network parameters for
                demonstration. Real parameters are fetched from TRON blockchain.
                <br />
                <strong>Consistency:</strong> This calculation now matches the
                bandwidth staking shown in all other components (KPIs, Recipient
                Comparison, etc.).
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="p-6 bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-xl border border-green-500/30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-green-500/20 rounded-xl">
              <Wifi className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <h4 className="text-xl font-bold text-white">
                Bandwidth Coverage Summary
              </h4>
              <p className="text-green-300">
                Your daily bandwidth requirements and staking solution
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-green-400">
              {paidBandwidth > 0 ? 'Staking Required' : 'Free Coverage'}
            </div>
            <div className="text-sm text-green-300">
              {paidBandwidth > 0 ? 'Beyond 600 free BP' : 'Within free limit'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="p-3 bg-white/5 rounded-lg border border-white/10">
            <div className="text-center">
              <div className="text-lg font-bold text-green-400 mb-1">
                {formatNumber(dailyBandwidthNeeded)} BP
              </div>
              <div className="text-green-200">Daily Bandwidth</div>
              <div className="text-xs text-green-300 mt-1">
                {transfersPerDay} transfers × {bytesPerTransfer} bytes
              </div>
            </div>
          </div>

          <div className="p-3 bg-white/5 rounded-lg border border-white/10">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-400 mb-1">
                {formatTrx(trxNeededForBandwidth)}
              </div>
              <div className="text-blue-200">TRX to Freeze</div>
              <div className="text-xs text-blue-300 mt-1">
                For additional bandwidth
              </div>
            </div>
          </div>

          <div className="p-3 bg-white/5 rounded-lg border border-white/10">
            <div className="text-center">
              <div className="text-lg font-bold text-purple-400 mb-1">
                {formatUsd(dailyCostWithoutStaking * trxPrice)}
              </div>
              <div className="text-purple-200">Daily Savings</div>
              <div className="text-xs text-purple-300 mt-1">
                When staking vs burning
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 p-4 bg-green-500/20 rounded-lg border border-green-500/30">
          <p className="text-sm text-green-200 leading-relaxed">
            <strong>🎯 Key Insight:</strong>{' '}
            {paidBandwidth > 0
              ? `To cover ${transfersPerDay} USDT transfers/day (${formatNumber(dailyBandwidthNeeded)} BP), you need ${formatNumber(paidBandwidth)} additional BP beyond the free 600 BP. This requires freezing ${formatTrx(trxNeededForBandwidth)} TRX for Bandwidth, making your transactions completely free from bandwidth costs.`
              : `Your ${transfersPerDay} USDT transfers/day only need ${formatNumber(dailyBandwidthNeeded)} BP, which is within the free 600 BP daily allowance. No additional TRX staking needed for bandwidth!`}
          </p>
          {paidBandwidth > 0 && (
            <div className="mt-3 p-3 bg-blue-500/20 rounded-lg border border-blue-500/30">
              <p className="text-xs text-blue-200 leading-relaxed">
                <strong>Expected Result:</strong> For 10 USDT transfers/day
                (3,500 BP), you need 2,900 additional BP beyond the free 600 BP.
                This requires approximately 2,471.71 TRX to stake for Bandwidth
                coverage.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Tooltip */}
      <div className="mt-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-yellow-400 mt-1 flex-shrink-0" />
          <div className="text-sm text-slate-300">
            <strong>💡 Important Notes:</strong> The free 600 BP refreshes
            daily. If you stake TRX for Bandwidth, your staked bandwidth
            allowance also refreshes daily. This calculation uses the current
            network parameters. For the most accurate results, ensure you have
            live network data.
          </div>
        </div>
      </div>
    </div>
  );
}
